/**
 * Иконка баннера — колокольчик (markup из `assets/icon/svg/bell.svg`). svgr на
 * проекте импортирует `.svg` как React-компонент, а `Notification.icon` ждёт URL
 * картинки, поэтому держим сырой markup строкой и оборачиваем в data-URL (приём
 * как в `faviconBadge.ts`). Stroke перекрашен в индиго под палитру проекта.
 */
const BELL_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none"><path d="M9.00195 17H5.60636C4.34793 17 3.71872 17 3.58633 16.9023C3.4376 16.7925 3.40126 16.7277 3.38515 16.5436C3.37082 16.3797 3.75646 15.7486 4.52776 14.4866C5.32411 13.1835 6.00031 11.2862 6.00031 8.6C6.00031 7.11479 6.63245 5.69041 7.75766 4.6402C8.88288 3.59 10.409 3 12.0003 3C13.5916 3 15.1177 3.59 16.2429 4.6402C17.3682 5.69041 18.0003 7.11479 18.0003 8.6C18.0003 11.2862 18.6765 13.1835 19.4729 14.4866C20.2441 15.7486 20.6298 16.3797 20.6155 16.5436C20.5994 16.7277 20.563 16.7925 20.4143 16.9023C20.2819 17 19.6527 17 18.3943 17H15.0003M9.00195 17L9.00031 18C9.00031 19.6569 10.3435 21 12.0003 21C13.6572 21 15.0003 19.6569 15.0003 18V17M9.00195 17H15.0003" stroke="#6366f1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

const BELL_ICON_URL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  BELL_ICON_SVG
)}`;

export interface IShowNativeNotificationParams {
  /** Заголовок баннера. */
  title: string;
  /** Тело баннера (доп. описание заказа). */
  body: string;
  /** Тег для схлопывания дублей (uuid заказа): тот же тег заменяет баннер. */
  tag: string;
  /** Колбэк по клику — навигация (роутер сервис не знает). */
  onClick: () => void;
}

/**
 * Поддерживает ли окружение Web Notification API. В Safari/некоторых вебвью
 * `Notification` может отсутствовать — в этом случае ведём себя тихо.
 *
 * @returns `true`, если `Notification` доступен в окне
 */
export const isNativeNotificationSupported = (): boolean =>
  typeof window !== 'undefined' && 'Notification' in window;

/**
 * Текущее разрешение браузера на показ уведомлений. Это состояние браузера —
 * мы им не управляем, только читаем. Из `denied` программно в `granted` не выйти.
 *
 * @returns `'granted'` / `'denied'` / `'default'` (или `'denied'`, если API нет)
 */
export const getNativePermission = (): NotificationPermission => {
  if (!isNativeNotificationSupported()) {
    return 'denied';
  }
  return Notification.permission;
};

/**
 * Запрашивает разрешение на уведомления. Вызывать ТОЛЬКО по жесту пользователя
 * (обработчик клика). Поддерживает и promise-, и старый callback-вариант API
 * без приведения типов.
 *
 * @returns итоговое разрешение браузера
 */
export const requestNativePermission =
  async (): Promise<NotificationPermission> => {
    if (!isNativeNotificationSupported()) {
      return 'denied';
    }

    const result = Notification.requestPermission();

    // Старый Safari: requestPermission принимает callback и возвращает void —
    // оборачиваем в промис, сужая по форме (без `as`).
    if (result instanceof Promise) {
      return result;
    }

    return new Promise<NotificationPermission>((resolve) => {
      Notification.requestPermission((permission) => {
        resolve(permission);
      });
    });
  };

/**
 * Показывает системный баннер. Вешает обработчик клика (фокус окна + переданный
 * колбэк навигации) и закрывает баннер после клика. Предполагается, что вызов
 * уже отфильтрован (права/тумблер/видимость проверены выше).
 *
 * @param params - заголовок, тело, тег и колбэк клика
 */
export const showNativeNotification = ({
  title,
  body,
  tag,
  onClick
}: IShowNativeNotificationParams): void => {
  if (!isNativeNotificationSupported() || getNativePermission() !== 'granted') {
    return;
  }

  const notification = new Notification(title, {
    body,
    tag,
    icon: BELL_ICON_URL
  });

  notification.onclick = () => {
    // Клик по уведомлению — пользовательский жест, фокус окна разрешён.
    window.focus();
    onClick();
    notification.close();
  };
};
